import React, { Fragment, useState } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import TaxTabs from '../user/TaxTabs';
import useAuthStore from '../../stores/authStore';
import { UserCircleIcon } from "@heroicons/react/24/outline";
import { UserIcon } from "@heroicons/react/24/outline";
import { API_BASE_URL } from '../../config/constants';
import Avatar from './Avatar';
import logo from '../../assets/irrf.png';
import BackgroundEffect from '../site/BackgroundEffect';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const DashboardLayout = ({ children, showTaxTabs = false, title = 'IRRF - Imposto de Renda Retido na Fonte' }) => {
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const logout = useAuthStore(state => state.logout);
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      // Erro tratado no store
    }
  };

  const formatCNPJ = (cnpj) => {
    if (!cnpj) return '';
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  };

  const UserMenu = () => {
    const user = useAuthStore(state => state.user);
    
    // Constrói a URL do avatar
    const avatarUrl = user?.avatarUrl;

    // Pega os dados do cliente
    const tradeName = user?.tradeName || user?.email;
    const cnpj = user?.cnpj || '00.000.000/0000-00';

    return (
      <Menu as="div" className="relative ml-3">
        <div>
          <Menu.Button className="flex items-center rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900 transition-colors border border-slate-700">
            <div className="mr-3 text-right">
              <div className="text-sm font-medium text-white">
                {tradeName}
              </div>
              <div className="text-xs text-gray-400">
                {formatCNPJ(cnpj)}
              </div>
            </div>
            <Avatar 
              url={avatarUrl}
              size="sm"
              alt={tradeName} 
            />
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-slate-800 ring-1 ring-black ring-opacity-5 focus:outline-none border border-slate-700">
            <Menu.Item>
              {({ active }) => (
                <Link
                  to={user?.role === 'ADMIN' ? '/admin/dashboard' : '/user/dashboard'}
                  className={classNames(
                    active ? 'bg-slate-700 text-white' : 'text-slate-300',
                    'block px-4 py-2 text-sm'
                  )}
                >
                  Dashboard
                </Link>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <Link
                  to="/profile"
                  className={classNames(
                    active ? 'bg-slate-700 text-white' : 'text-slate-300',
                    'block px-4 py-2 text-sm'
                  )}
                >
                  Perfil
                </Link>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <Link
                  to="/profile"
                  state={{ openPayerSourceModal: true }}
                  className={classNames(
                    active ? 'bg-slate-700 text-white' : 'text-slate-300',
                    'block px-4 py-2 text-sm'
                  )}
                >
                  Cadastrar Fonte Pagadora
                </Link>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={handleLogout}
                  className={classNames(
                    active ? 'bg-slate-700 text-white' : 'text-slate-300',
                    'block w-full text-left px-4 py-2 text-sm'
                  )}
                >
                  Sair
                </button>
              )}
            </Menu.Item>
          </Menu.Items>
        </Transition>
      </Menu>
    );
  };

  return (
    <div className="min-h-screen text-slate-200 relative">
      <BackgroundEffect />
      <nav className="bg-slate-900 border-b border-slate-800 shadow-lg relative z-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 justify-between">
            <div className="flex">
              <div className="flex flex-shrink-0 items-center">
                <img
                  className="block h-10 w-auto lg:hidden"
                  src={logo}
                  alt="IRRF"
                />
                <img
                  className="hidden h-10 w-auto lg:block"
                  src={logo}
                  alt="IRRF"
                />
              </div>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:items-center">
              <UserMenu />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-0">
        {showTaxTabs && <TaxTabs />}
        <div className="p-4">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
